import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, vShow as _vShow, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, resolveComponent as _resolveComponent, withModifiers as _withModifiers, Transition as _Transition } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-sm text-red-500"
}
const _hoisted_2 = { class: "mt-1 relative" }
const _hoisted_3 = {
  key: 0,
  class: "text-sm text-red-500"
}
const _hoisted_4 = { class: "mt-1 relative" }
const _hoisted_5 = { class: "absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" }

import {
		ref,
		computed,
		nextTick,
		watch,
		PropType
	} from 'vue'
	
	import {
		Combobox,
		ComboboxButton,
		ComboboxInput,
		ComboboxLabel,
		ComboboxOption,
		ComboboxOptions,
		Listbox,
		ListboxButton,
		ListboxLabel,
		ListboxOption,
		ListboxOptions,
		TransitionRoot
	} from '@headlessui/vue'

	import {
		CheckIcon,
		ChevronUpDownIcon
	} from '@heroicons/vue/24/solid'
	// #endregion


	// #region setup
	
export default /*#__PURE__*/_defineComponent({
  __name: 'TwSelect',
  props: {
		modelValue: {
			type: [String, Number, Array],
			default: null
		},
		clearable: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		filterable: {
			type: Boolean,
			default: false
		},
		filterPlaceholder: {
			type: String,
			default: "Type to filter list..."
		},
		label: {
			type: String,
			default: ''
		},
		multiple: {
			type: Boolean,
			default: false
		},
		noResultsPlaceholder: {
			type: String,
			default: "Nothing found."
		},
		options: {
			type: Array as PropType<Array<{label: string,value: string}>>,
			default: () => []
		},
		placeholder: {
			type: String,
			default: "None selected"
		},
		required: {
			type: Boolean,
			default: false
		},
		requiredError: {
			type: String,
			default: "This field is required."
		},
		searchable: {
			type: Boolean,
			default: false
		},
		searchMethod: {
			type: Function,
			default: () => {return}
		},
		searchPlaceholder: {
			type: String,
			default: "Type to search..."
		},
		valid: {
			type: Boolean,
			default: true
		},
		validationError: {
			type: String,
			default: "This field is not valid."
		}
	},
  emits: [
		'update:modelValue'
	],
  setup(__props, { expose: __expose, emit: __emit }) {

// #region imports
	const emit = __emit

	const props = __props
	// #endregion


	// #region editing model value
	/**
	 * Text to display in the select box. Formats multiple selections as a comma separated string.
	 * @yields {string}
	 */
	const modelLabel = computed(() => {
		if(Array.isArray(props.modelValue)) {
			return props.modelValue.join(', ')
		}
		else{
			return props.modelValue
		}
	})

	/**
	 * Updates the bound value of the select menu
	 * @function onSelect
	 * @param {Object} option - the selected option of the select menu
	 */
	async function onSelect(value: any) {
		emit('update:modelValue', value)
		await nextTick()
		validate()
		return
	}

	/**
	 * Clears the selected value
	 * @function clear
	 */
	async function clear() {
		if(Array.isArray(props.modelValue)) {
			emit('update:modelValue', [])
		}
		else {
			emit('update:modelValue', '')
		}
		await nextTick()
		return
	}
	// #endregion

	// #region validation
	const _valid = ref(props.valid)
	watch(props, () => {
		_valid.value = props.valid
	})
	function validate() {
		if((props.required && !props.modelValue) || props.valid === false) {
			_valid.value = false
		}
		else if(props.required && props.modelValue) {
			_valid.value = true
		}
	}
	// #endregion


	// #region filtering
	const query = ref('')

	function updateQuery(e) {
		query.value = e.target.value
		if(props.searchable) {
			props.searchMethod(query.value)
		}
	}

	const _placeholder = ref(props.placeholder)

	/**
     * Updates the placeholder text when filtering or searching and the combobox is focused
     * @function setPlaceholder
     */
	function setPlaceholder() {
		if(props.filterable) _placeholder.value = props.filterPlaceholder
		else if(props.searchable) _placeholder.value = props.searchPlaceholder
	}

	/**
     * Clears the placeholder text when focus is lost on the combobox
     * @function setPlaceholder
     */
	function clearPlaceholder() {
		_placeholder.value = props.placeholder
	}

	/**
	 * List of options filtered by filter text
	 * @yields {array}
	 */
	const filteredOptions = computed(() => {
		return query.value === '' ? props.options : props.options.filter((option: any) => {
			return !props.filterable || !query.value || option.label.toLowerCase().replace(/\s+/g, '').includes(query.value.toLowerCase().replace(/\s+/g, '')) == true
		})
	})

	/**
	 * If no options remain when filtered by filter text
	 * @yields {boolean}
	 */
	const noFilteredOptions = computed(() => {
		return filteredOptions.value.length === 0 && query.value !== ''
	})
	// #endregion

	__expose({
		validate
	})


return (_ctx: any,_cache: any) => {
  const _component_XCircleIcon = _resolveComponent("XCircleIcon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (__props.searchable || __props.filterable)
      ? (_openBlock(), _createBlock(_unref(Combobox), {
          key: 0,
          as: "div",
          "model-value": __props.modelValue,
          multiple: __props.multiple,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = value => onSelect(value))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ComboboxLabel), { class: "block text-sm font-medium text-gray-700" }, {
              default: _withCtx(() => [
                (__props.required)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_1, " * "))
                  : _createCommentVNode("", true),
                _createElementVNode("span", null, _toDisplayString(__props.label), 1)
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(ComboboxInput), {
                id: "comboBoxInput",
                class: _normalizeClass(["relative w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-left shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:text-sm", {
						'bg-gray-200 cursor-not-allowed pointer-events-none': __props.disabled, 
						'bg-white cursor-default': !__props.disabled,
						'border-0 pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500': !_valid.value
					}]),
                onFocus: setPlaceholder,
                onBlur: clearPlaceholder,
                autocomplete: "none",
                onChange: _cache[0] || (_cache[0] = ($event: any) => (updateQuery($event))),
                "display-value": (v) => Array.isArray(v) ? v.join(', ') : String(v),
                placeholder: _placeholder.value
              }, null, 8, ["class", "display-value", "placeholder"]),
              _createVNode(_unref(ComboboxButton), { class: "absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none" }, {
                default: _withCtx(() => [
                  _withDirectives(_createVNode(_unref(ChevronUpDownIcon), {
                    class: "h-5 w-5 text-gray-400",
                    "aria-hidden": "true"
                  }, null, 512), [
                    [_vShow, !__props.disabled]
                  ])
                ]),
                _: 1
              }),
              _createVNode(_unref(TransitionRoot), {
                leave: "transition ease-in duration-100",
                "leave-from": "opacity-100",
                "leave-to": "opacity-0",
                onAfterLeave: _cache[1] || (_cache[1] = ($event: any) => (query.value = ''))
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ComboboxOptions), { class: "absolute z-30 mt-1 w-full bg-white shadow-lg max-h-60 rounded-sm py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" }, {
                    default: _withCtx(() => [
                      _withDirectives(_createElementVNode("div", {
                        as: "template",
                        class: "relative cursor-default select-none py-2 px-4 text-gray-700"
                      }, _toDisplayString(__props.noResultsPlaceholder), 513), [
                        [_vShow, noFilteredOptions.value]
                      ]),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((__props.filterable ? filteredOptions.value : __props.options), (option) => {
                        return (_openBlock(), _createBlock(_unref(ComboboxOption), {
                          as: "template",
                          key: option.value,
                          value: option.value
                        }, {
                          default: _withCtx(({ active, selected }) => [
                            _createElementVNode("li", {
                              class: _normalizeClass([active ? 'text-white bg-brand-500' : 'text-gray-900', 'cursor-default select-none relative py-2 pl-3 pr-9'])
                            }, [
                              _createElementVNode("span", {
                                class: _normalizeClass([selected ? 'font-semibold' : 'font-normal', 'block truncate'])
                              }, _toDisplayString(option.label), 3),
                              selected
                                ? (_openBlock(), _createElementBlock("span", {
                                    key: 0,
                                    class: _normalizeClass([active ? 'text-white' : 'text-brand-500', 'absolute inset-y-0 right-0 flex items-center pr-4'])
                                  }, [
                                    _createVNode(_unref(CheckIcon), {
                                      class: "h-5 w-5",
                                      "aria-hidden": "true"
                                    })
                                  ], 2))
                                : _createCommentVNode("", true)
                            ], 2)
                          ]),
                          _: 2
                        }, 1032, ["value"]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        }, 8, ["model-value", "multiple"]))
      : (_openBlock(), _createBlock(_unref(Listbox), {
          key: 1,
          as: "div",
          "model-value": __props.modelValue,
          multiple: __props.multiple,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = value => onSelect(value))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ListboxLabel), { class: "block text-sm font-medium text-gray-700" }, {
              default: _withCtx(() => [
                (__props.required)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_3, " * "))
                  : _createCommentVNode("", true),
                _createElementVNode("span", null, _toDisplayString(__props.label), 1)
              ]),
              _: 1
            }),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(_unref(ListboxButton), {
                id: "listBoxButton",
                class: _normalizeClass(["relative w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-left shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:text-sm", {
						'bg-gray-200 cursor-not-allowed pointer-events-none': __props.disabled, 
						'bg-white cursor-default': !__props.disabled,
						'border-0 pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500': !_valid.value
					}])
              }, {
                default: _withCtx(() => [
                  _createElementVNode("span", {
                    class: _normalizeClass(["block truncate", {'text-gray-600': !modelLabel.value}])
                  }, _toDisplayString(modelLabel.value || _placeholder.value), 3),
                  _createElementVNode("span", _hoisted_5, [
                    (__props.clearable)
                      ? _withDirectives((_openBlock(), _createBlock(_component_XCircleIcon, {
                          key: 0,
                          class: "h-5 w-5 text-red-500 hover:text-red-700 pointer-events-auto",
                          "aria-hidden": "true",
                          onClick: _withModifiers(clear, ["stop"])
                        }, null, 512)), [
                          [_vShow, !__props.disabled && modelLabel.value]
                        ])
                      : _createCommentVNode("", true),
                    _withDirectives(_createVNode(_unref(ChevronUpDownIcon), {
                      class: "h-5 w-5 text-gray-400",
                      "aria-hidden": "true"
                    }, null, 512), [
                      [_vShow, !__props.disabled]
                    ])
                  ])
                ]),
                _: 1
              }, 8, ["class"]),
              _createVNode(_Transition, {
                "leave-active-class": "transition ease-in duration-100",
                "leave-from-class": "opacity-100",
                "leave-to-class": "opacity-0"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(ListboxOptions), { class: "absolute z-30 mt-1 w-full bg-white shadow-lg max-h-60 rounded-sm py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm" }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.options, (option) => {
                        return (_openBlock(), _createBlock(_unref(ListboxOption), {
                          as: "template",
                          key: option.value,
                          value: option.value
                        }, {
                          default: _withCtx(({ active, selected }) => [
                            _createElementVNode("li", {
                              class: _normalizeClass([active ? 'text-white bg-brand-500' : 'text-gray-900', 'cursor-default select-none relative py-2 pl-3 pr-9'])
                            }, [
                              _createElementVNode("span", {
                                class: _normalizeClass([selected ? 'font-semibold' : 'font-normal', 'block truncate'])
                              }, _toDisplayString(option.label), 3),
                              selected
                                ? (_openBlock(), _createElementBlock("span", {
                                    key: 0,
                                    class: _normalizeClass([active ? 'text-white' : 'text-brand-500', 'absolute inset-y-0 right-0 flex items-center pr-4'])
                                  }, [
                                    _createVNode(_unref(CheckIcon), {
                                      class: "h-5 w-5",
                                      "aria-hidden": "true"
                                    })
                                  ], 2))
                                : _createCommentVNode("", true)
                            ], 2)
                          ]),
                          _: 2
                        }, 1032, ["value"]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ])
          ]),
          _: 1
        }, 8, ["model-value", "multiple"])),
    _withDirectives(_createElementVNode("p", { class: "mt-2 text-sm text-red-600" }, _toDisplayString(props.required && !props.modelValue ? __props.requiredError : __props.validationError), 513), [
      [_vShow, !_valid.value]
    ])
  ]))
}
}

})